import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import DynamicTable from "../../../Components/Common/DynamicTable";
import { restructureData } from "../../../utils/restructureData";
import countryData from "../../../Pages/Masters/Roles and Permissions/Country.json";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI415 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  const [inputData, setInputData] = useState([
    {
      fieldName: "politicalContributionsByCountry",
      title:
        "a. Could you provide the total monetary value of financial and in-kind political contributions made directly and indirectly by the organization, categorized by country and recipient/beneficiary?",
      fields: [
        {
          fieldName: "country",
          title: "Country",
          type: "select",
          options: [], // Placeholder for country options
          placeholder: "Select a country",
        },
        {
          fieldName: "recipient",
          title: "Recipient / Beneficiary",
          type: "text",
          placeholder: "Type here",
        },
        {
          fieldName: "contribution",
          title: "Contribution Type (Financial / in-kind)",
          type: "select",
          options: ["Financial", "in-kind"],
          placeholder: "Select a type",
        },
        {
          fieldName: "monetaryValue",
          title: "Monetary Value (in USD)",
          type: "text",
          placeholder: "Enter amount",
        },
        {
          fieldName: "contributionMethod",
          title: "Contribution Method (Direct / Indirect)",
          type: "select",
          options: ["Direct", "Indirect"],
          placeholder: "Select a method",
        },
      ],
    },
  ]);

  useEffect(() => {
    // Populate country options dynamically
    const countries = countryData.countries.map((country) => country.name);
    setInputData((prevInputData) =>
      prevInputData.map((section) => {
        if (section.fieldName === "politicalContributionsByCountry") {
          return {
            ...section,
            fields: section.fields.map((field) =>
              field.fieldName === "country"
                ? { ...field, options: countries }
                : field
            ),
          };
        }
        return section;
      })
    );
  }, []);

  const [tables, setTables] = useState([]);

  const [tabErrors, setTabErrors] = useState({});
  const [data, setData] = useState({});

  const [formData, setFormData] = useState({
    financialPoliticalContributions: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        const data1 = response.data.data.GRI400.publicPolicy || {};
        setData(data1);
        setFormData({
          financialPoliticalContributions:
            data1?.financialPoliticalContributions || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    if (!formData.financialPoliticalContributions) {
      newErrors.financialPoliticalContributions = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // const isTableValid = validateTable(inputData, tables, setTabErrors);

      // if (
      //   // !validateForm() ||
      //   !isTableValid) {
      //   return;
      // }

      let transformedData = restructureData(tables);

      const payload = {
        type: "GRI400",
        submitted415: true,
        publicPolicy: {
          ...formData,
          ...transformedData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/416-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/416-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      <p className={styles.section_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>415 : Public Policy</p>
      <p className={styles.main_heading} style={{ marginBottom: hide ? "-1.5rem" : "0" }}>415-1 : Political contributions</p>
      <Box>
        <Box>
          <DynamicTable
            inputData={inputData}
            tables={tables}
            data={data}
            setTables={setTables}
            errors={tabErrors}
            setErrors={setTabErrors}
            disabled={disabled}
            hide={hide}
          />
        </Box>

        <SectionHeading text="b. Additionally, if applicable, please explain how the monetary value of in-kind contributions was estimated." />

        <CustomTextField
          value={formData.financialPoliticalContributions}
          onChange={(e) =>
            handleInputChange("financialPoliticalContributions", e.target.value)
          }
          error={errors.financialPoliticalContributions}
          helperText={errors.financialPoliticalContributions}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI415;
